import React, { createContext, useContext, useEffect, useState } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { Device } from '@capacitor/device';
import { StatusBar, Style } from '@capacitor/status-bar';
import { useDispatch } from 'react-redux';
import { setNativeInfo, setKeyboardVisible } from '../store/native.reducer';

const NativeContext = createContext();

export const useNative = () => {
  const context = useContext(NativeContext);
  if (!context) {
    throw new Error('useNative must be used within NativeProvider');
  }
  return context;
};

export const NativeProvider = ({ children }) => {
  const dispatch = useDispatch();
  const [isNative] = useState(() => Capacitor.isNativePlatform());
  const [platform] = useState(() => Capacitor.getPlatform());
  const [deviceInfo, setDeviceInfo] = useState(null);

  useEffect(() => {
    const initNative = async () => {
      if (isNative) {
        try {
          const info = await Device.getInfo();
          setDeviceInfo(info);
          dispatch(setNativeInfo({ isNative: true, platform: info.platform, deviceInfo: info }));
          Keyboard.addListener('keyboardWillShow', () => dispatch(setKeyboardVisible(true)));
          Keyboard.addListener('keyboardWillHide', () => dispatch(setKeyboardVisible(false)));
          if (info.platform === 'ios') {
            try {
              await StatusBar.setOverlaysWebView({ overlay: true });
              await StatusBar.setStyle({ style: Style.Dark });
            } catch (statusBarError) {
              console.error('Error initializing iOS status bar:', statusBarError);
            }
          }
        } catch (error) {
          console.error('Error initializing native features:', error);
        }
      } else {
        dispatch(setNativeInfo({ isNative: false, platform: 'web', deviceInfo: null }));
      }
    };
    initNative();
    return () => {
      if (isNative) Keyboard.removeAllListeners();
    };
  }, [isNative, dispatch]);

  return (
    <NativeContext.Provider value={{ isNative, platform, deviceInfo }}>
      {children}
    </NativeContext.Provider>
  );
};
